export type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

export type Database = {
  // Allows to automatically instantiate createClient with right options
  // instead of createClient<Database, { PostgrestVersion: 'XX' }>(URL, KEY)
  __InternalSupabase: {
    PostgrestVersion: "14.15";
  };
  graphql_public: {
    Tables: {
      [_ in never]: never;
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      graphql: {
        Args: {
          extensions?: Json;
          operationName?: string;
          query?: string;
          variables?: Json;
        };
        Returns: Json;
      };
    };
    Enums: {
      [_ in never]: never;
    };
    CompositeTypes: {
      [_ in never]: never;
    };
  };
  public: {
    Tables: {
      audit_logs: {
        Row: {
          action: string;
          created_at: string | null;
          details: string | null;
          id: number;
          request_id: number | null;
          user_id: string | null;
        };
        Insert: {
          action: string;
          created_at?: string | null;
          details?: string | null;
          id?: never;
          request_id?: number | null;
          user_id?: string | null;
        };
        Update: {
          action?: string;
          created_at?: string | null;
          details?: string | null;
          id?: never;
          request_id?: number | null;
          user_id?: string | null;
        };
        Relationships: [];
      };
      banners: {
        Row: {
          active: boolean;
          audience: string[];
          created_at: string | null;
          expires_at: string | null;
          id: string;
          message: string;
          states: string[] | null;
          style: string;
        };
        Insert: {
          active?: boolean;
          audience?: string[];
          created_at?: string | null;
          expires_at?: string | null;
          id?: string;
          message: string;
          states?: string[] | null;
          style?: string;
        };
        Update: {
          active?: boolean;
          audience?: string[];
          created_at?: string | null;
          expires_at?: string | null;
          id?: string;
          message?: string;
          states?: string[] | null;
          style?: string;
        };
        Relationships: [];
      };
      calendar_event_exceptions: {
        Row: {
          all_day: boolean | null;
          cancelled: boolean;
          color: string | null;
          created_at: string;
          created_by: string | null;
          deactivated: boolean;
          description: string | null;
          ends_at: string | null;
          event_id: string;
          id: string;
          occurrence_date: string;
          staff_id: string | null;
          starts_at: string | null;
          title: string | null;
          updated_at: string;
        };
        Insert: {
          all_day?: boolean | null;
          cancelled?: boolean;
          color?: string | null;
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          description?: string | null;
          ends_at?: string | null;
          event_id: string;
          id?: string;
          occurrence_date: string;
          staff_id?: string | null;
          starts_at?: string | null;
          title?: string | null;
          updated_at?: string;
        };
        Update: {
          all_day?: boolean | null;
          cancelled?: boolean;
          color?: string | null;
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          description?: string | null;
          ends_at?: string | null;
          event_id?: string;
          id?: string;
          occurrence_date?: string;
          staff_id?: string | null;
          starts_at?: string | null;
          title?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "calendar_event_exceptions_event_id_fkey";
            columns: ["event_id"];
            isOneToOne: false;
            referencedRelation: "calendar_events";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "calendar_event_exceptions_staff_id_fkey";
            columns: ["staff_id"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      calendar_events: {
        Row: {
          all_day: boolean;
          color: string | null;
          created_at: string;
          created_by: string | null;
          deactivated: boolean;
          description: string | null;
          ends_at: string;
          id: string;
          recur_until: string | null;
          recurrence: string;
          staff_id: string | null;
          starts_at: string;
          title: string;
          updated_at: string;
        };
        Insert: {
          all_day?: boolean;
          color?: string | null;
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          description?: string | null;
          ends_at: string;
          id?: string;
          recur_until?: string | null;
          recurrence?: string;
          staff_id?: string | null;
          starts_at: string;
          title: string;
          updated_at?: string;
        };
        Update: {
          all_day?: boolean;
          color?: string | null;
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          description?: string | null;
          ends_at?: string;
          id?: string;
          recur_until?: string | null;
          recurrence?: string;
          staff_id?: string | null;
          starts_at?: string;
          title?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "calendar_events_staff_id_fkey";
            columns: ["staff_id"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      children_devices: {
        Row: {
          approval_status: Database["public"]["Enums"]["approval_status_enum"] | null;
          byod: string | null;
          children_id: string;
          confirmed_at: string | null;
          course_name: string | null;
          delivery_address: string | null;
          device_order_number: string | null;
          device_product_code: string | null;
          device_so_number: string | null;
          device_type: Database["public"]["Enums"]["device_type_enum"] | null;
          estimated_cost: number | null;
          has_school_device: boolean | null;
          id: string;
          internet_safety: boolean | null;
          reason: string | null;
          requested_amount: number | null;
          tafe_course: string | null;
          tafe_length: string | null;
          tafe_start_date: string | null;
          terms_accepted: boolean | null;
          why_needed: string | null;
        };
        Insert: {
          approval_status?: Database["public"]["Enums"]["approval_status_enum"] | null;
          byod?: string | null;
          children_id: string;
          confirmed_at?: string | null;
          course_name?: string | null;
          delivery_address?: string | null;
          device_order_number?: string | null;
          device_product_code?: string | null;
          device_so_number?: string | null;
          device_type?: Database["public"]["Enums"]["device_type_enum"] | null;
          estimated_cost?: number | null;
          has_school_device?: boolean | null;
          id?: string;
          internet_safety?: boolean | null;
          reason?: string | null;
          requested_amount?: number | null;
          tafe_course?: string | null;
          tafe_length?: string | null;
          tafe_start_date?: string | null;
          terms_accepted?: boolean | null;
          why_needed?: string | null;
        };
        Update: {
          approval_status?: Database["public"]["Enums"]["approval_status_enum"] | null;
          byod?: string | null;
          children_id?: string;
          confirmed_at?: string | null;
          course_name?: string | null;
          delivery_address?: string | null;
          device_order_number?: string | null;
          device_product_code?: string | null;
          device_so_number?: string | null;
          device_type?: Database["public"]["Enums"]["device_type_enum"] | null;
          estimated_cost?: number | null;
          has_school_device?: boolean | null;
          id?: string;
          internet_safety?: boolean | null;
          reason?: string | null;
          requested_amount?: number | null;
          tafe_course?: string | null;
          tafe_length?: string | null;
          tafe_start_date?: string | null;
          terms_accepted?: boolean | null;
          why_needed?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "child_laptops_child_id_fkey";
            columns: ["children_id"];
            isOneToOne: true;
            referencedRelation: "request_children";
            referencedColumns: ["id"];
          },
        ];
      };
      children_fees: {
        Row: {
          approval_status: Database["public"]["Enums"]["approval_status_enum"] | null;
          brochure: string | null;
          children_id: string;
          confirmed_at: string | null;
          fee_amount: number | null;
          fee_desc: string | null;
          fee_index: number;
          fee_type: Database["public"]["Enums"]["fee_type_enum"];
          fees_order_number: string | null;
          fees_so_number: string | null;
          id: string;
          reason: string | null;
          requested_amount: number | null;
        };
        Insert: {
          approval_status?: Database["public"]["Enums"]["approval_status_enum"] | null;
          brochure?: string | null;
          children_id: string;
          confirmed_at?: string | null;
          fee_amount?: number | null;
          fee_desc?: string | null;
          fee_index?: number;
          fee_type: Database["public"]["Enums"]["fee_type_enum"];
          fees_order_number?: string | null;
          fees_so_number?: string | null;
          id?: string;
          reason?: string | null;
          requested_amount?: number | null;
        };
        Update: {
          approval_status?: Database["public"]["Enums"]["approval_status_enum"] | null;
          brochure?: string | null;
          children_id?: string;
          confirmed_at?: string | null;
          fee_amount?: number | null;
          fee_desc?: string | null;
          fee_index?: number;
          fee_type?: Database["public"]["Enums"]["fee_type_enum"];
          fees_order_number?: string | null;
          fees_so_number?: string | null;
          id?: string;
          reason?: string | null;
          requested_amount?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "child_fees_child_id_fkey";
            columns: ["children_id"];
            isOneToOne: false;
            referencedRelation: "request_children";
            referencedColumns: ["id"];
          },
        ];
      };
      children_shoes: {
        Row: {
          approval_status: Database["public"]["Enums"]["approval_status_enum"] | null;
          children_id: string;
          confirmed_at: string | null;
          id: string;
          jogger_cost: number | null;
          jogger_qty: number | null;
          leather_cost: number | null;
          leather_qty: number | null;
          reason: string | null;
          requested_amount: number | null;
        };
        Insert: {
          approval_status?: Database["public"]["Enums"]["approval_status_enum"] | null;
          children_id: string;
          confirmed_at?: string | null;
          id?: string;
          jogger_cost?: number | null;
          jogger_qty?: number | null;
          leather_cost?: number | null;
          leather_qty?: number | null;
          reason?: string | null;
          requested_amount?: number | null;
        };
        Update: {
          approval_status?: Database["public"]["Enums"]["approval_status_enum"] | null;
          children_id?: string;
          confirmed_at?: string | null;
          id?: string;
          jogger_cost?: number | null;
          jogger_qty?: number | null;
          leather_cost?: number | null;
          leather_qty?: number | null;
          reason?: string | null;
          requested_amount?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "child_shoes_child_id_fkey";
            columns: ["children_id"];
            isOneToOne: true;
            referencedRelation: "request_children";
            referencedColumns: ["id"];
          },
        ];
      };
      children_uniforms: {
        Row: {
          approval_status: Database["public"]["Enums"]["approval_status_enum"] | null;
          authorised_contact: string | null;
          authorised_person: string | null;
          children_id: string;
          confirmed_at: string | null;
          custom_items: Json | null;
          email: string | null;
          id: string;
          is_cold_climate: boolean | null;
          is_reimbursement: boolean | null;
          items: Json | null;
          mobile: string | null;
          osd_code: string | null;
          osd_received_at: string | null;
          osd_source_email_id: string | null;
          other: string | null;
          reason: string | null;
          requested_amount: number | null;
          supplier: string | null;
        };
        Insert: {
          approval_status?: Database["public"]["Enums"]["approval_status_enum"] | null;
          authorised_contact?: string | null;
          authorised_person?: string | null;
          children_id: string;
          confirmed_at?: string | null;
          custom_items?: Json | null;
          email?: string | null;
          id?: string;
          is_cold_climate?: boolean | null;
          is_reimbursement?: boolean | null;
          items?: Json | null;
          mobile?: string | null;
          osd_code?: string | null;
          osd_received_at?: string | null;
          osd_source_email_id?: string | null;
          other?: string | null;
          reason?: string | null;
          requested_amount?: number | null;
          supplier?: string | null;
        };
        Update: {
          approval_status?: Database["public"]["Enums"]["approval_status_enum"] | null;
          authorised_contact?: string | null;
          authorised_person?: string | null;
          children_id?: string;
          confirmed_at?: string | null;
          custom_items?: Json | null;
          email?: string | null;
          id?: string;
          is_cold_climate?: boolean | null;
          is_reimbursement?: boolean | null;
          items?: Json | null;
          mobile?: string | null;
          osd_code?: string | null;
          osd_received_at?: string | null;
          osd_source_email_id?: string | null;
          other?: string | null;
          reason?: string | null;
          requested_amount?: number | null;
          supplier?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "child_uniforms_child_id_fkey";
            columns: ["children_id"];
            isOneToOne: true;
            referencedRelation: "request_children";
            referencedColumns: ["id"];
          },
        ];
      };
      comment_templates: {
        Row: {
          appends_next_steps: string | null;
          category: string;
          created_at: string | null;
          deactivated: boolean;
          id: string;
          text: string;
          title: string;
        };
        Insert: {
          appends_next_steps?: string | null;
          category: string;
          created_at?: string | null;
          deactivated?: boolean;
          id: string;
          text: string;
          title: string;
        };
        Update: {
          appends_next_steps?: string | null;
          category?: string;
          created_at?: string | null;
          deactivated?: boolean;
          id?: string;
          text?: string;
          title?: string;
        };
        Relationships: [];
      };
      crm_campaign_recipients: {
        Row: {
          campaign_id: number;
          error: string | null;
          id: number;
          recipient_email: string;
          recipient_name: string | null;
          sent_at: string | null;
          shelter_code: string | null;
          smtp_message_id: string | null;
          status: string;
          unsub_token: string;
        };
        Insert: {
          campaign_id: number;
          error?: string | null;
          id?: never;
          recipient_email: string;
          recipient_name?: string | null;
          sent_at?: string | null;
          shelter_code?: string | null;
          smtp_message_id?: string | null;
          status?: string;
          unsub_token?: string;
        };
        Update: {
          campaign_id?: number;
          error?: string | null;
          id?: never;
          recipient_email?: string;
          recipient_name?: string | null;
          sent_at?: string | null;
          shelter_code?: string | null;
          smtp_message_id?: string | null;
          status?: string;
          unsub_token?: string;
        };
        Relationships: [
          {
            foreignKeyName: "crm_campaign_recipients_campaign_id_fkey";
            columns: ["campaign_id"];
            isOneToOne: false;
            referencedRelation: "crm_campaigns";
            referencedColumns: ["id"];
          },
        ];
      };
      crm_campaigns: {
        Row: {
          attachment_name: string | null;
          attachment_path: string | null;
          audience_mode: string | null;
          body_html: string;
          body_text: string | null;
          created_at: string;
          created_by: string | null;
          deactivated: boolean;
          dispatch_attempts: number;
          dispatch_lock_at: string | null;
          dispatch_started_at: string | null;
          from_label: string | null;
          id: number;
          name: string | null;
          parent_id: number | null;
          recipient_filter: Json | null;
          reply_to: string | null;
          scheduled_at: string | null;
          sent_at: string | null;
          status: string;
          subject: string;
          total_recipients: number;
          version: number;
          version_group: string;
        };
        Insert: {
          attachment_name?: string | null;
          attachment_path?: string | null;
          audience_mode?: string | null;
          body_html: string;
          body_text?: string | null;
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          dispatch_attempts?: number;
          dispatch_lock_at?: string | null;
          dispatch_started_at?: string | null;
          from_label?: string | null;
          id?: never;
          name?: string | null;
          parent_id?: number | null;
          recipient_filter?: Json | null;
          reply_to?: string | null;
          scheduled_at?: string | null;
          sent_at?: string | null;
          status?: string;
          subject: string;
          total_recipients?: number;
          version?: number;
          version_group?: string;
        };
        Update: {
          attachment_name?: string | null;
          attachment_path?: string | null;
          audience_mode?: string | null;
          body_html?: string;
          body_text?: string | null;
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          dispatch_attempts?: number;
          dispatch_lock_at?: string | null;
          dispatch_started_at?: string | null;
          from_label?: string | null;
          id?: never;
          name?: string | null;
          parent_id?: number | null;
          recipient_filter?: Json | null;
          reply_to?: string | null;
          scheduled_at?: string | null;
          sent_at?: string | null;
          status?: string;
          subject?: string;
          total_recipients?: number;
          version?: number;
          version_group?: string;
        };
        Relationships: [
          {
            foreignKeyName: "crm_campaigns_parent_id_fkey";
            columns: ["parent_id"];
            isOneToOne: false;
            referencedRelation: "crm_campaigns";
            referencedColumns: ["id"];
          },
        ];
      };
      crm_unsubscribes: {
        Row: {
          email: string;
          id: number;
          note: string | null;
          reactivated_at: string | null;
          reactivated_by: string | null;
          source: string | null;
          unsubscribed_at: string;
        };
        Insert: {
          email: string;
          id?: never;
          note?: string | null;
          reactivated_at?: string | null;
          reactivated_by?: string | null;
          source?: string | null;
          unsubscribed_at?: string;
        };
        Update: {
          email?: string;
          id?: never;
          note?: string | null;
          reactivated_at?: string | null;
          reactivated_by?: string | null;
          source?: string | null;
          unsubscribed_at?: string;
        };
        Relationships: [];
      };
      delivery_addresses: {
        Row: {
          address_name: string | null;
          banned_address: boolean;
          created_at: string | null;
          deactivated: boolean;
          delivery_instruction: string | null;
          id: string;
          is_default: boolean | null;
          postcode: string | null;
          shelter_id: string;
          silent_address: boolean;
          state: string | null;
          street: string | null;
          street2: string | null;
          suburb: string | null;
          suburb_city: string | null;
          use_count: number;
          zephyr_verified: boolean;
        };
        Insert: {
          address_name?: string | null;
          banned_address?: boolean;
          created_at?: string | null;
          deactivated?: boolean;
          delivery_instruction?: string | null;
          id?: string;
          is_default?: boolean | null;
          postcode?: string | null;
          shelter_id: string;
          silent_address?: boolean;
          state?: string | null;
          street?: string | null;
          street2?: string | null;
          suburb?: string | null;
          suburb_city?: string | null;
          use_count?: number;
          zephyr_verified?: boolean;
        };
        Update: {
          address_name?: string | null;
          banned_address?: boolean;
          created_at?: string | null;
          deactivated?: boolean;
          delivery_instruction?: string | null;
          id?: string;
          is_default?: boolean | null;
          postcode?: string | null;
          shelter_id?: string;
          silent_address?: boolean;
          state?: string | null;
          street?: string | null;
          street2?: string | null;
          suburb?: string | null;
          suburb_city?: string | null;
          use_count?: number;
          zephyr_verified?: boolean;
        };
        Relationships: [
          {
            foreignKeyName: "fk_delivery_addresses_shelter_id";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      help_content: {
        Row: {
          key: string;
          text: string;
          updated_at: string | null;
          updated_by: string | null;
        };
        Insert: {
          key: string;
          text?: string;
          updated_at?: string | null;
          updated_by?: string | null;
        };
        Update: {
          key?: string;
          text?: string;
          updated_at?: string | null;
          updated_by?: string | null;
        };
        Relationships: [];
      };
      lookup_options: {
        Row: {
          app_visible: boolean;
          category: string;
          created_at: string;
          deactivated: boolean;
          external_ref: string | null;
          id: string;
          is_system: boolean;
          option_category: string | null;
          sort_order: number;
          value: string;
        };
        Insert: {
          app_visible?: boolean;
          category: string;
          created_at?: string;
          deactivated?: boolean;
          external_ref?: string | null;
          id?: string;
          is_system?: boolean;
          option_category?: string | null;
          sort_order?: number;
          value: string;
        };
        Update: {
          app_visible?: boolean;
          category?: string;
          created_at?: string;
          deactivated?: boolean;
          external_ref?: string | null;
          id?: string;
          is_system?: boolean;
          option_category?: string | null;
          sort_order?: number;
          value?: string;
        };
        Relationships: [];
      };
      polling_log: {
        Row: {
          alerts_sent: number | null;
          duration_ms: number | null;
          emails_seen: number | null;
          errors: number | null;
          id: number;
          matches: number | null;
          message: string | null;
          ran_at: string;
          status: string;
        };
        Insert: {
          alerts_sent?: number | null;
          duration_ms?: number | null;
          emails_seen?: number | null;
          errors?: number | null;
          id?: number;
          matches?: number | null;
          message?: string | null;
          ran_at?: string;
          status: string;
        };
        Update: {
          alerts_sent?: number | null;
          duration_ms?: number | null;
          emails_seen?: number | null;
          errors?: number | null;
          id?: number;
          matches?: number | null;
          message?: string | null;
          ran_at?: string;
          status?: string;
        };
        Relationships: [];
      };
      portal_config: {
        Row: {
          key: string;
          updated_at: string | null;
          value: Json;
        };
        Insert: {
          key: string;
          updated_at?: string | null;
          value: Json;
        };
        Update: {
          key?: string;
          updated_at?: string | null;
          value?: Json;
        };
        Relationships: [];
      };
      profiles: {
        Row: {
          admin_permissions: Json | null;
          created_at: string | null;
          deactivated: boolean | null;
          display_name: string | null;
          id: string;
          invited_at: string | null;
          is_consultant: boolean;
          is_generic: boolean | null;
          login_method: string;
          mfa_enabled: boolean | null;
          preferred_name: string | null;
          role: string;
          show_on_staff_list: boolean;
          staff_colour: string | null;
          staff_first_name: string | null;
          staff_last_name: string | null;
          staff_mobile: string | null;
          staff_notes: string | null;
          staff_user_email: string | null;
          updated_at: string | null;
          updated_by: string | null;
          welcome_dismissed: boolean | null;
        };
        Insert: {
          admin_permissions?: Json | null;
          created_at?: string | null;
          deactivated?: boolean | null;
          display_name?: string | null;
          id: string;
          invited_at?: string | null;
          is_consultant?: boolean;
          is_generic?: boolean | null;
          login_method?: string;
          mfa_enabled?: boolean | null;
          preferred_name?: string | null;
          role: string;
          show_on_staff_list?: boolean;
          staff_colour?: string | null;
          staff_first_name?: string | null;
          staff_last_name?: string | null;
          staff_mobile?: string | null;
          staff_notes?: string | null;
          staff_user_email?: string | null;
          updated_at?: string | null;
          updated_by?: string | null;
          welcome_dismissed?: boolean | null;
        };
        Update: {
          admin_permissions?: Json | null;
          created_at?: string | null;
          deactivated?: boolean | null;
          display_name?: string | null;
          id?: string;
          invited_at?: string | null;
          is_consultant?: boolean;
          is_generic?: boolean | null;
          login_method?: string;
          mfa_enabled?: boolean | null;
          preferred_name?: string | null;
          role?: string;
          show_on_staff_list?: boolean;
          staff_colour?: string | null;
          staff_first_name?: string | null;
          staff_last_name?: string | null;
          staff_mobile?: string | null;
          staff_notes?: string | null;
          staff_user_email?: string | null;
          updated_at?: string | null;
          updated_by?: string | null;
          welcome_dismissed?: boolean | null;
        };
        Relationships: [];
      };
      push_log: {
        Row: {
          duration_ms: number | null;
          failed: number | null;
          id: number;
          message: string | null;
          pushed: number | null;
          ran_at: string;
          retried: number | null;
          status: string;
        };
        Insert: {
          duration_ms?: number | null;
          failed?: number | null;
          id?: number;
          message?: string | null;
          pushed?: number | null;
          ran_at?: string;
          retried?: number | null;
          status: string;
        };
        Update: {
          duration_ms?: number | null;
          failed?: number | null;
          id?: number;
          message?: string | null;
          pushed?: number | null;
          ran_at?: string;
          retried?: number | null;
          status?: string;
        };
        Relationships: [];
      };
      request_children: {
        Row: {
          child_index: number;
          created_at: string | null;
          gender: string | null;
          id: string;
          request_id: number;
          school_id: number | null;
          school_name: string | null;
          year_level: string | null;
        };
        Insert: {
          child_index: number;
          created_at?: string | null;
          gender?: string | null;
          id?: string;
          request_id: number;
          school_id?: number | null;
          school_name?: string | null;
          year_level?: string | null;
        };
        Update: {
          child_index?: number;
          created_at?: string | null;
          gender?: string | null;
          id?: string;
          request_id?: number;
          school_id?: number | null;
          school_name?: string | null;
          year_level?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "children_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "children_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "request_children_school_id_fkey";
            columns: ["school_id"];
            isOneToOne: false;
            referencedRelation: "schools";
            referencedColumns: ["id"];
          },
        ];
      };
      request_decisions: {
        Row: {
          category: string;
          child_index: number;
          confirmed_at: string;
          decided_at: string;
          decided_by: string | null;
          fee_index: number | null;
          from_status: string | null;
          id: number;
          item_amount: number | null;
          item_label: string | null;
          reason: string | null;
          reason_category: string | null;
          request_id: number;
          to_status: string;
        };
        Insert: {
          category: string;
          child_index: number;
          confirmed_at: string;
          decided_at?: string;
          decided_by?: string | null;
          fee_index?: number | null;
          from_status?: string | null;
          id?: never;
          item_amount?: number | null;
          item_label?: string | null;
          reason?: string | null;
          reason_category?: string | null;
          request_id: number;
          to_status: string;
        };
        Update: {
          category?: string;
          child_index?: number;
          confirmed_at?: string;
          decided_at?: string;
          decided_by?: string | null;
          fee_index?: number | null;
          from_status?: string | null;
          id?: never;
          item_amount?: number | null;
          item_label?: string | null;
          reason?: string | null;
          reason_category?: string | null;
          request_id?: number;
          to_status?: string;
        };
        Relationships: [
          {
            foreignKeyName: "request_decisions_decided_by_fkey";
            columns: ["decided_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "request_decisions_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "request_decisions_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
        ];
      };
      request_internal_notes: {
        Row: {
          author: string;
          author_id: string | null;
          created_at: string;
          id: number;
          kind: string;
          request_id: number;
          text: string;
        };
        Insert: {
          author: string;
          author_id?: string | null;
          created_at?: string;
          id?: never;
          kind?: string;
          request_id: number;
          text: string;
        };
        Update: {
          author?: string;
          author_id?: string | null;
          created_at?: string;
          id?: never;
          kind?: string;
          request_id?: number;
          text?: string;
        };
        Relationships: [
          {
            foreignKeyName: "request_internal_notes_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "request_internal_notes_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
        ];
      };
      request_picker_notifications: {
        Row: {
          id: string;
          item_type: string;
          request_id: number;
          sent_at: string;
          sent_by: string | null;
          sent_date: string;
        };
        Insert: {
          id?: string;
          item_type: string;
          request_id: number;
          sent_at?: string;
          sent_by?: string | null;
          sent_date?: string;
        };
        Update: {
          id?: string;
          item_type?: string;
          request_id?: number;
          sent_at?: string;
          sent_by?: string | null;
          sent_date?: string;
        };
        Relationships: [
          {
            foreignKeyName: "request_picker_notifications_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "request_picker_notifications_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
        ];
      };
      request_sales_order_pushes: {
        Row: {
          api_excluded: boolean;
          attempt_count: number;
          created_at: string;
          error_message: string | null;
          exported_at: string | null;
          id: number;
          next_attempt_at: string | null;
          order_number: string;
          payee_identifier: string | null;
          pushed_at: string | null;
          request_id: number;
          request_payload: Json | null;
          response_payload: Json | null;
          status: string;
          suffix: string | null;
          suffix_sequence: number | null;
          unleashed_guid: string | null;
        };
        Insert: {
          api_excluded?: boolean;
          attempt_count?: number;
          created_at?: string;
          error_message?: string | null;
          exported_at?: string | null;
          id?: never;
          next_attempt_at?: string | null;
          order_number: string;
          payee_identifier?: string | null;
          pushed_at?: string | null;
          request_id: number;
          request_payload?: Json | null;
          response_payload?: Json | null;
          status?: string;
          suffix?: string | null;
          suffix_sequence?: number | null;
          unleashed_guid?: string | null;
        };
        Update: {
          api_excluded?: boolean;
          attempt_count?: number;
          created_at?: string;
          error_message?: string | null;
          exported_at?: string | null;
          id?: never;
          next_attempt_at?: string | null;
          order_number?: string;
          payee_identifier?: string | null;
          pushed_at?: string | null;
          request_id?: number;
          request_payload?: Json | null;
          response_payload?: Json | null;
          status?: string;
          suffix?: string | null;
          suffix_sequence?: number | null;
          unleashed_guid?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "request_sales_order_pushes_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "request_sales_order_pushes_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
        ];
      };
      request_sales_orders: {
        Row: {
          comments: string | null;
          created_at: string;
          customer_code: string;
          customer_reference: string | null;
          delivery_city: string | null;
          delivery_contact: string | null;
          delivery_country: string | null;
          delivery_instruction: string | null;
          delivery_name: string | null;
          delivery_post_code: string | null;
          delivery_region: string | null;
          delivery_street_address: string | null;
          delivery_street_address_2: string | null;
          delivery_suburb: string | null;
          discount_percent: number | null;
          exchange_rate: number | null;
          id: number;
          line_comments: string | null;
          line_discount_percent: number | null;
          line_number: number;
          order_date: string | null;
          order_number: string;
          order_quantity: number;
          product_code: string;
          quote_expiry_date: string | null;
          request_id: number;
          required_date: string | null;
          sales_order_group: string | null;
          sales_person_email: string | null;
          tax_code: string | null;
          tax_rate: number | null;
          unit_price: number | null;
          warehouse_code: string | null;
        };
        Insert: {
          comments?: string | null;
          created_at?: string;
          customer_code: string;
          customer_reference?: string | null;
          delivery_city?: string | null;
          delivery_contact?: string | null;
          delivery_country?: string | null;
          delivery_instruction?: string | null;
          delivery_name?: string | null;
          delivery_post_code?: string | null;
          delivery_region?: string | null;
          delivery_street_address?: string | null;
          delivery_street_address_2?: string | null;
          delivery_suburb?: string | null;
          discount_percent?: number | null;
          exchange_rate?: number | null;
          id?: never;
          line_comments?: string | null;
          line_discount_percent?: number | null;
          line_number: number;
          order_date?: string | null;
          order_number: string;
          order_quantity: number;
          product_code: string;
          quote_expiry_date?: string | null;
          request_id: number;
          required_date?: string | null;
          sales_order_group?: string | null;
          sales_person_email?: string | null;
          tax_code?: string | null;
          tax_rate?: number | null;
          unit_price?: number | null;
          warehouse_code?: string | null;
        };
        Update: {
          comments?: string | null;
          created_at?: string;
          customer_code?: string;
          customer_reference?: string | null;
          delivery_city?: string | null;
          delivery_contact?: string | null;
          delivery_country?: string | null;
          delivery_instruction?: string | null;
          delivery_name?: string | null;
          delivery_post_code?: string | null;
          delivery_region?: string | null;
          delivery_street_address?: string | null;
          delivery_street_address_2?: string | null;
          delivery_suburb?: string | null;
          discount_percent?: number | null;
          exchange_rate?: number | null;
          id?: never;
          line_comments?: string | null;
          line_discount_percent?: number | null;
          line_number?: number;
          order_date?: string | null;
          order_number?: string;
          order_quantity?: number;
          product_code?: string;
          quote_expiry_date?: string | null;
          request_id?: number;
          required_date?: string | null;
          sales_order_group?: string | null;
          sales_person_email?: string | null;
          tax_code?: string | null;
          tax_rate?: number | null;
          unit_price?: number | null;
          warehouse_code?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "request_sales_orders_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "request_sales_orders_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
        ];
      };
      request_tasks: {
        Row: {
          assigned_at: string | null;
          assigned_to: string | null;
          assigned_to_id: string | null;
          completed_at: string | null;
          created_at: string | null;
          description: string | null;
          id: string;
          ref_no: string | null;
          release_note: string | null;
          request_id: number | null;
          shelter_code: string | null;
          shelter_name: string | null;
          so_number: string | null;
          state: string | null;
          task_notes: Json | null;
          type: string;
        };
        Insert: {
          assigned_at?: string | null;
          assigned_to?: string | null;
          assigned_to_id?: string | null;
          completed_at?: string | null;
          created_at?: string | null;
          description?: string | null;
          id?: string;
          ref_no?: string | null;
          release_note?: string | null;
          request_id?: number | null;
          shelter_code?: string | null;
          shelter_name?: string | null;
          so_number?: string | null;
          state?: string | null;
          task_notes?: Json | null;
          type: string;
        };
        Update: {
          assigned_at?: string | null;
          assigned_to?: string | null;
          assigned_to_id?: string | null;
          completed_at?: string | null;
          created_at?: string | null;
          description?: string | null;
          id?: string;
          ref_no?: string | null;
          release_note?: string | null;
          request_id?: number | null;
          shelter_code?: string | null;
          shelter_name?: string | null;
          so_number?: string | null;
          state?: string | null;
          task_notes?: Json | null;
          type?: string;
        };
        Relationships: [
          {
            foreignKeyName: "request_tasks_assigned_to_id_fkey";
            columns: ["assigned_to_id"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "tasks_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "tasks_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
        ];
      };
      requests: {
        Row: {
          approvals_confirmed_at: string | null;
          caseworker_note: string | null;
          cc_emails: Json | null;
          children: Json | null;
          contact_name: string | null;
          created_at: string | null;
          date: string | null;
          deactivated: boolean;
          delivery_address_id: string | null;
          delivery_address_override: string | null;
          docs: Json | null;
          id: number;
          invoices: Json | null;
          mobile: string | null;
          non_registered_address: boolean | null;
          notes: Json | null;
          phone: string | null;
          ref_no: string;
          request_type: string;
          shelter_address: string | null;
          shelter_email: string | null;
          shelter_has_unread_comment: boolean;
          shelter_id: string;
          shelter_name: string | null;
          shelter_ref: string | null;
          state: string | null;
          status: Database["public"]["Enums"]["request_status_enum"] | null;
          submitted_by: string | null;
          submitted_by_name: string | null;
          updated_at: string | null;
          updated_by: string | null;
          updated_by_role: string | null;
        };
        Insert: {
          approvals_confirmed_at?: string | null;
          caseworker_note?: string | null;
          cc_emails?: Json | null;
          children?: Json | null;
          contact_name?: string | null;
          created_at?: string | null;
          date?: string | null;
          deactivated?: boolean;
          delivery_address_id?: string | null;
          delivery_address_override?: string | null;
          docs?: Json | null;
          id?: number;
          invoices?: Json | null;
          mobile?: string | null;
          non_registered_address?: boolean | null;
          notes?: Json | null;
          phone?: string | null;
          ref_no: string;
          request_type?: string;
          shelter_address?: string | null;
          shelter_email?: string | null;
          shelter_has_unread_comment?: boolean;
          shelter_id: string;
          shelter_name?: string | null;
          shelter_ref?: string | null;
          state?: string | null;
          status?: Database["public"]["Enums"]["request_status_enum"] | null;
          submitted_by?: string | null;
          submitted_by_name?: string | null;
          updated_at?: string | null;
          updated_by?: string | null;
          updated_by_role?: string | null;
        };
        Update: {
          approvals_confirmed_at?: string | null;
          caseworker_note?: string | null;
          cc_emails?: Json | null;
          children?: Json | null;
          contact_name?: string | null;
          created_at?: string | null;
          date?: string | null;
          deactivated?: boolean;
          delivery_address_id?: string | null;
          delivery_address_override?: string | null;
          docs?: Json | null;
          id?: number;
          invoices?: Json | null;
          mobile?: string | null;
          non_registered_address?: boolean | null;
          notes?: Json | null;
          phone?: string | null;
          ref_no?: string;
          request_type?: string;
          shelter_address?: string | null;
          shelter_email?: string | null;
          shelter_has_unread_comment?: boolean;
          shelter_id?: string;
          shelter_name?: string | null;
          shelter_ref?: string | null;
          state?: string | null;
          status?: Database["public"]["Enums"]["request_status_enum"] | null;
          submitted_by?: string | null;
          submitted_by_name?: string | null;
          updated_at?: string | null;
          updated_by?: string | null;
          updated_by_role?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "fk_requests_delivery_address_id";
            columns: ["delivery_address_id"];
            isOneToOne: false;
            referencedRelation: "delivery_addresses";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "fk_requests_shelter_id";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      resource_documents: {
        Row: {
          created_at: string;
          deactivated: boolean;
          file_path: string;
          id: string;
          mime_type: string | null;
          name: string;
          size_bytes: number | null;
          updated_at: string;
          uploaded_by: string | null;
        };
        Insert: {
          created_at?: string;
          deactivated?: boolean;
          file_path: string;
          id?: string;
          mime_type?: string | null;
          name: string;
          size_bytes?: number | null;
          updated_at?: string;
          uploaded_by?: string | null;
        };
        Update: {
          created_at?: string;
          deactivated?: boolean;
          file_path?: string;
          id?: string;
          mime_type?: string | null;
          name?: string;
          size_bytes?: number | null;
          updated_at?: string;
          uploaded_by?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "resource_documents_uploaded_by_fkey";
            columns: ["uploaded_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      resource_pages: {
        Row: {
          body: string | null;
          created_at: string;
          created_by: string | null;
          deactivated: boolean;
          id: string;
          parent_id: string | null;
          section: string;
          sort_order: number;
          title: string;
          updated_at: string;
        };
        Insert: {
          body?: string | null;
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          id?: string;
          parent_id?: string | null;
          section: string;
          sort_order?: number;
          title: string;
          updated_at?: string;
        };
        Update: {
          body?: string | null;
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          id?: string;
          parent_id?: string | null;
          section?: string;
          sort_order?: number;
          title?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "resource_pages_parent_id_fkey";
            columns: ["parent_id"];
            isOneToOne: false;
            referencedRelation: "resource_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      school_byod: {
        Row: {
          byod_checked_at: string | null;
          byod_device: string | null;
          byod_flags: Json | null;
          byod_hardship: boolean | null;
          byod_loan: boolean | null;
          byod_loan_note: string | null;
          byod_notes: string | null;
          byod_policy_url: string | null;
          byod_policy_year: number | null;
          byod_source: string | null;
          byod_status: string | null;
          created_at: string;
          deactivated: boolean;
          deactivated_at: string | null;
          id: string;
          school_id: number;
          year_from: number | null;
          year_to: number | null;
        };
        Insert: {
          byod_checked_at?: string | null;
          byod_device?: string | null;
          byod_flags?: Json | null;
          byod_hardship?: boolean | null;
          byod_loan?: boolean | null;
          byod_loan_note?: string | null;
          byod_notes?: string | null;
          byod_policy_url?: string | null;
          byod_policy_year?: number | null;
          byod_source?: string | null;
          byod_status?: string | null;
          created_at?: string;
          deactivated?: boolean;
          deactivated_at?: string | null;
          id?: string;
          school_id: number;
          year_from?: number | null;
          year_to?: number | null;
        };
        Update: {
          byod_checked_at?: string | null;
          byod_device?: string | null;
          byod_flags?: Json | null;
          byod_hardship?: boolean | null;
          byod_loan?: boolean | null;
          byod_loan_note?: string | null;
          byod_notes?: string | null;
          byod_policy_url?: string | null;
          byod_policy_year?: number | null;
          byod_source?: string | null;
          byod_status?: string | null;
          created_at?: string;
          deactivated?: boolean;
          deactivated_at?: string | null;
          id?: string;
          school_id?: number;
          year_from?: number | null;
          year_to?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "school_byod_school_id_fkey";
            columns: ["school_id"];
            isOneToOne: false;
            referencedRelation: "schools";
            referencedColumns: ["id"];
          },
        ];
      };
      school_locker_submissions: {
        Row: {
          email_body_snapshot: string | null;
          failure_reason: string | null;
          id: string;
          ref_no: string;
          request_id: number | null;
          smtp2go_message_id: string | null;
          status: string;
          submitted_at: string;
          submitted_by: string;
          timeout_alert_sent_at: string | null;
        };
        Insert: {
          email_body_snapshot?: string | null;
          failure_reason?: string | null;
          id?: string;
          ref_no: string;
          request_id?: number | null;
          smtp2go_message_id?: string | null;
          status: string;
          submitted_at?: string;
          submitted_by: string;
          timeout_alert_sent_at?: string | null;
        };
        Update: {
          email_body_snapshot?: string | null;
          failure_reason?: string | null;
          id?: string;
          ref_no?: string;
          request_id?: number | null;
          smtp2go_message_id?: string | null;
          status?: string;
          submitted_at?: string;
          submitted_by?: string;
          timeout_alert_sent_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "school_locker_submissions_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "school_locker_submissions_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
        ];
      };
      schools: {
        Row: {
          acara_id: string | null;
          deactivated: boolean;
          id: number;
          name: string;
          postcode: string | null;
          school_sl: boolean;
          school_type: string | null;
          state: string | null;
          suburb: string | null;
        };
        Insert: {
          acara_id?: string | null;
          deactivated?: boolean;
          id?: number;
          name: string;
          postcode?: string | null;
          school_sl?: boolean;
          school_type?: string | null;
          state?: string | null;
          suburb?: string | null;
        };
        Update: {
          acara_id?: string | null;
          deactivated?: boolean;
          id?: number;
          name?: string;
          postcode?: string | null;
          school_sl?: boolean;
          school_type?: string | null;
          state?: string | null;
          suburb?: string | null;
        };
        Relationships: [];
      };
      section_blocks: {
        Row: {
          created_at: string;
          created_by: string | null;
          deactivated: boolean;
          end_date: string;
          id: string;
          message: string;
          section: string;
          start_date: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          end_date: string;
          id?: string;
          message: string;
          section: string;
          start_date: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          end_date?: string;
          id?: string;
          message?: string;
          section?: string;
          start_date?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      shelter_bank_details: {
        Row: {
          bank_account: string | null;
          bank_name: string | null;
          created_at: string;
          id: string;
          shelter_id: string;
          updated_at: string;
          updated_by: string | null;
        };
        Insert: {
          bank_account?: string | null;
          bank_name?: string | null;
          created_at?: string;
          id?: string;
          shelter_id: string;
          updated_at?: string;
          updated_by?: string | null;
        };
        Update: {
          bank_account?: string | null;
          bank_name?: string | null;
          created_at?: string;
          id?: string;
          shelter_id?: string;
          updated_at?: string;
          updated_by?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "shelter_bank_details_shelter_id_fkey";
            columns: ["shelter_id"];
            isOneToOne: true;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      shelter_change_log: {
        Row: {
          changed_at: string;
          changed_by: string | null;
          changes: Json;
          id: string;
          shelter_id: string;
          source_table: string;
        };
        Insert: {
          changed_at?: string;
          changed_by?: string | null;
          changes: Json;
          id?: string;
          shelter_id: string;
          source_table: string;
        };
        Update: {
          changed_at?: string;
          changed_by?: string | null;
          changes?: Json;
          id?: string;
          shelter_id?: string;
          source_table?: string;
        };
        Relationships: [
          {
            foreignKeyName: "shelter_change_log_changed_by_fkey";
            columns: ["changed_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "shelter_change_log_shelter_id_fkey";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      shelter_feedback: {
        Row: {
          attachment_name: string | null;
          attachment_path: string | null;
          created_at: string;
          date_received: string | null;
          deactivated: boolean;
          id: number;
          memo: string;
          reviewed: boolean;
          shelter_code: string | null;
          shelter_id: string;
          submitted_by: string | null;
          title: string | null;
        };
        Insert: {
          attachment_name?: string | null;
          attachment_path?: string | null;
          created_at?: string;
          date_received?: string | null;
          deactivated?: boolean;
          id?: never;
          memo: string;
          reviewed?: boolean;
          shelter_code?: string | null;
          shelter_id: string;
          submitted_by?: string | null;
          title?: string | null;
        };
        Update: {
          attachment_name?: string | null;
          attachment_path?: string | null;
          created_at?: string;
          date_received?: string | null;
          deactivated?: boolean;
          id?: never;
          memo?: string;
          reviewed?: boolean;
          shelter_code?: string | null;
          shelter_id?: string;
          submitted_by?: string | null;
          title?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "shelter_feedback_shelter_id_fkey";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      shelter_notes: {
        Row: {
          id: string;
          no_approvals: boolean | null;
          notes: string | null;
          shelter_code: string | null;
          shelter_id: string | null;
          shelter_name: string;
          updated_at: string | null;
          updated_by: string | null;
        };
        Insert: {
          id?: string;
          no_approvals?: boolean | null;
          notes?: string | null;
          shelter_code?: string | null;
          shelter_id?: string | null;
          shelter_name: string;
          updated_at?: string | null;
          updated_by?: string | null;
        };
        Update: {
          id?: string;
          no_approvals?: boolean | null;
          notes?: string | null;
          shelter_code?: string | null;
          shelter_id?: string | null;
          shelter_name?: string;
          updated_at?: string | null;
          updated_by?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "fk_shelter_notes_shelter_id";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      shelters: {
        Row: {
          abn: string | null;
          created_at: string | null;
          customer_type: string | null;
          date_onboarded: string | null;
          deactivated: boolean | null;
          default_warehouse: string | null;
          dfv_service_portal_notes: string | null;
          id: string;
          internal_notes: string | null;
          physical_line1: string | null;
          physical_line2: string | null;
          physical_postcode: string | null;
          physical_state: string | null;
          physical_suburb: string | null;
          physical_suburb_city: string | null;
          postal_line1: string | null;
          postal_line2: string | null;
          postal_postcode: string | null;
          postal_state: string | null;
          postal_suburb: string | null;
          postal_suburb_city: string | null;
          primary_contact: string | null;
          reminder: string | null;
          sales_group: string | null;
          shelter_code: string;
          shelter_name: string;
          state: string | null;
          stock_held: boolean;
          stop_credit: boolean;
          taxable: boolean | null;
          unleashed_guid: string | null;
          updated_at: string | null;
          updated_by: string | null;
        };
        Insert: {
          abn?: string | null;
          created_at?: string | null;
          customer_type?: string | null;
          date_onboarded?: string | null;
          deactivated?: boolean | null;
          default_warehouse?: string | null;
          dfv_service_portal_notes?: string | null;
          id?: string;
          internal_notes?: string | null;
          physical_line1?: string | null;
          physical_line2?: string | null;
          physical_postcode?: string | null;
          physical_state?: string | null;
          physical_suburb?: string | null;
          physical_suburb_city?: string | null;
          postal_line1?: string | null;
          postal_line2?: string | null;
          postal_postcode?: string | null;
          postal_state?: string | null;
          postal_suburb?: string | null;
          postal_suburb_city?: string | null;
          primary_contact?: string | null;
          reminder?: string | null;
          sales_group?: string | null;
          shelter_code: string;
          shelter_name: string;
          state?: string | null;
          stock_held?: boolean;
          stop_credit?: boolean;
          taxable?: boolean | null;
          unleashed_guid?: string | null;
          updated_at?: string | null;
          updated_by?: string | null;
        };
        Update: {
          abn?: string | null;
          created_at?: string | null;
          customer_type?: string | null;
          date_onboarded?: string | null;
          deactivated?: boolean | null;
          default_warehouse?: string | null;
          dfv_service_portal_notes?: string | null;
          id?: string;
          internal_notes?: string | null;
          physical_line1?: string | null;
          physical_line2?: string | null;
          physical_postcode?: string | null;
          physical_state?: string | null;
          physical_suburb?: string | null;
          physical_suburb_city?: string | null;
          postal_line1?: string | null;
          postal_line2?: string | null;
          postal_postcode?: string | null;
          postal_state?: string | null;
          postal_suburb?: string | null;
          postal_suburb_city?: string | null;
          primary_contact?: string | null;
          reminder?: string | null;
          sales_group?: string | null;
          shelter_code?: string;
          shelter_name?: string;
          state?: string | null;
          stock_held?: boolean;
          stop_credit?: boolean;
          taxable?: boolean | null;
          unleashed_guid?: string | null;
          updated_at?: string | null;
          updated_by?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "shelters_primary_contact_fkey";
            columns: ["primary_contact"];
            isOneToOne: false;
            referencedRelation: "user_shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      sister_group_members: {
        Row: {
          created_at: string;
          deactivated: boolean;
          deactivated_at: string | null;
          id: string;
          shelter_id: string;
          sister_group_id: string;
        };
        Insert: {
          created_at?: string;
          deactivated?: boolean;
          deactivated_at?: string | null;
          id?: string;
          shelter_id: string;
          sister_group_id: string;
        };
        Update: {
          created_at?: string;
          deactivated?: boolean;
          deactivated_at?: string | null;
          id?: string;
          shelter_id?: string;
          sister_group_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "sister_group_members_shelter_id_fkey";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "sister_group_members_sister_group_id_fkey";
            columns: ["sister_group_id"];
            isOneToOne: false;
            referencedRelation: "sister_groups";
            referencedColumns: ["id"];
          },
        ];
      };
      sister_groups: {
        Row: {
          created_at: string;
          deactivated: boolean;
          deactivated_at: string | null;
          id: string;
          name: string;
        };
        Insert: {
          created_at?: string;
          deactivated?: boolean;
          deactivated_at?: string | null;
          id?: string;
          name: string;
        };
        Update: {
          created_at?: string;
          deactivated?: boolean;
          deactivated_at?: string | null;
          id?: string;
          name?: string;
        };
        Relationships: [];
      };
      so_rules: {
        Row: {
          active: boolean;
          created_at: string;
          customer_reference: string | null;
          group_by: string;
          id: number;
          label: string;
          line_comment_template: string | null;
          line_mode: string;
          match_fee_types: string[] | null;
          match_kind: string;
          match_payment_method: string | null;
          match_state: string | null;
          match_supplier_kind: string | null;
          match_supplier_suffix: string | null;
          payee_type: string | null;
          priority: number;
          requires_picker: boolean;
          suffix: string | null;
          updated_at: string;
        };
        Insert: {
          active?: boolean;
          created_at?: string;
          customer_reference?: string | null;
          group_by: string;
          id?: never;
          label: string;
          line_comment_template?: string | null;
          line_mode: string;
          match_fee_types?: string[] | null;
          match_kind: string;
          match_payment_method?: string | null;
          match_state?: string | null;
          match_supplier_kind?: string | null;
          match_supplier_suffix?: string | null;
          payee_type?: string | null;
          priority: number;
          requires_picker?: boolean;
          suffix?: string | null;
          updated_at?: string;
        };
        Update: {
          active?: boolean;
          created_at?: string;
          customer_reference?: string | null;
          group_by?: string;
          id?: never;
          label?: string;
          line_comment_template?: string | null;
          line_mode?: string;
          match_fee_types?: string[] | null;
          match_kind?: string;
          match_payment_method?: string | null;
          match_state?: string | null;
          match_supplier_kind?: string | null;
          match_supplier_suffix?: string | null;
          payee_type?: string | null;
          priority?: number;
          requires_picker?: boolean;
          suffix?: string | null;
          updated_at?: string;
        };
        Relationships: [];
      };
      stock_items: {
        Row: {
          created_at: string;
          deactivated: boolean;
          id: string;
          max_qty: number;
          name: string;
          pack_size: number;
          sort_order: number;
        };
        Insert: {
          created_at?: string;
          deactivated?: boolean;
          id?: string;
          max_qty: number;
          name: string;
          pack_size: number;
          sort_order?: number;
        };
        Update: {
          created_at?: string;
          deactivated?: boolean;
          id?: string;
          max_qty?: number;
          name?: string;
          pack_size?: number;
          sort_order?: number;
        };
        Relationships: [];
      };
      stock_top_up_headers: {
        Row: {
          created_at: string;
          note: string | null;
          request_id: number;
        };
        Insert: {
          created_at?: string;
          note?: string | null;
          request_id: number;
        };
        Update: {
          created_at?: string;
          note?: string | null;
          request_id?: number;
        };
        Relationships: [
          {
            foreignKeyName: "stock_top_up_headers_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: true;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "stock_top_up_headers_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: true;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
        ];
      };
      stock_top_up_lines: {
        Row: {
          amount: number | null;
          approval_status: string;
          approved_qty: number | null;
          colour: string | null;
          created_at: string;
          id: string;
          item_key: string;
          item_name: string | null;
          level: string | null;
          qty_on_hand: number | null;
          qty_required: number | null;
          request_id: number;
          section: string;
          size: string | null;
          stock_item_id: string | null;
          year_level: string | null;
        };
        Insert: {
          amount?: number | null;
          approval_status?: string;
          approved_qty?: number | null;
          colour?: string | null;
          created_at?: string;
          id?: string;
          item_key: string;
          item_name?: string | null;
          level?: string | null;
          qty_on_hand?: number | null;
          qty_required?: number | null;
          request_id: number;
          section: string;
          size?: string | null;
          stock_item_id?: string | null;
          year_level?: string | null;
        };
        Update: {
          amount?: number | null;
          approval_status?: string;
          approved_qty?: number | null;
          colour?: string | null;
          created_at?: string;
          id?: string;
          item_key?: string;
          item_name?: string | null;
          level?: string | null;
          qty_on_hand?: number | null;
          qty_required?: number | null;
          request_id?: number;
          section?: string;
          size?: string | null;
          stock_item_id?: string | null;
          year_level?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "stock_top_up_lines_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "stock_top_up_lines_request_id_fkey";
            columns: ["request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "stock_top_up_lines_stock_item_id_fkey";
            columns: ["stock_item_id"];
            isOneToOne: false;
            referencedRelation: "stock_items";
            referencedColumns: ["id"];
          },
        ];
      };
      stock_top_up_prefill: {
        Row: {
          consumed_at: string | null;
          consumed_request_id: number | null;
          created_at: string;
          id: string;
          note: string | null;
          payload: Json;
          prepared_by: string | null;
          shelter_id: string;
          status: string;
        };
        Insert: {
          consumed_at?: string | null;
          consumed_request_id?: number | null;
          created_at?: string;
          id?: string;
          note?: string | null;
          payload?: Json;
          prepared_by?: string | null;
          shelter_id: string;
          status?: string;
        };
        Update: {
          consumed_at?: string | null;
          consumed_request_id?: number | null;
          created_at?: string;
          id?: string;
          note?: string | null;
          payload?: Json;
          prepared_by?: string | null;
          shelter_id?: string;
          status?: string;
        };
        Relationships: [
          {
            foreignKeyName: "stock_top_up_prefill_consumed_request_id_fkey";
            columns: ["consumed_request_id"];
            isOneToOne: false;
            referencedRelation: "requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "stock_top_up_prefill_consumed_request_id_fkey";
            columns: ["consumed_request_id"];
            isOneToOne: false;
            referencedRelation: "requests_view";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "stock_top_up_prefill_prepared_by_fkey";
            columns: ["prepared_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "stock_top_up_prefill_shelter_id_fkey";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      suppliers: {
        Row: {
          abn: string | null;
          account_terms: string | null;
          action_sheet_override: string | null;
          created_at: string;
          deactivated: boolean;
          default_warehouse_code: string | null;
          doc_name: string | null;
          doc_template: string | null;
          document_type: string;
          dropdown_label: string | null;
          email: string | null;
          first_name: string | null;
          footer_message: string | null;
          has_authority_doc: boolean;
          id: number;
          is_obsoleted: boolean;
          is_uniform_supplier: boolean;
          kind: string;
          last_name: string | null;
          logo_path: string | null;
          minimum_order_value: number | null;
          mobile_number: string | null;
          name: string;
          notes: string | null;
          office_phone: string | null;
          payment_method: string;
          phone_number: string | null;
          physical_address_name: string | null;
          physical_city: string | null;
          physical_country: string | null;
          physical_postal_code: string | null;
          physical_region: string | null;
          physical_street_address: string | null;
          physical_street_address_2: string | null;
          physical_suburb: string | null;
          postal_address_name: string | null;
          postal_city: string | null;
          postal_country: string | null;
          postal_postal_code: string | null;
          postal_region: string | null;
          postal_street_address: string | null;
          postal_street_address_2: string | null;
          postal_suburb: string | null;
          reminder: string | null;
          so_suffix: string | null;
          supplier_code: string;
          tax_code: string | null;
          taxable: boolean | null;
          unleashed_product_code: string | null;
          updated_at: string;
          website: string | null;
          zephyr_account_number: string | null;
        };
        Insert: {
          abn?: string | null;
          account_terms?: string | null;
          action_sheet_override?: string | null;
          created_at?: string;
          deactivated?: boolean;
          default_warehouse_code?: string | null;
          doc_name?: string | null;
          doc_template?: string | null;
          document_type?: string;
          dropdown_label?: string | null;
          email?: string | null;
          first_name?: string | null;
          footer_message?: string | null;
          has_authority_doc?: boolean;
          id?: never;
          is_obsoleted?: boolean;
          is_uniform_supplier?: boolean;
          kind?: string;
          last_name?: string | null;
          logo_path?: string | null;
          minimum_order_value?: number | null;
          mobile_number?: string | null;
          name: string;
          notes?: string | null;
          office_phone?: string | null;
          payment_method?: string;
          phone_number?: string | null;
          physical_address_name?: string | null;
          physical_city?: string | null;
          physical_country?: string | null;
          physical_postal_code?: string | null;
          physical_region?: string | null;
          physical_street_address?: string | null;
          physical_street_address_2?: string | null;
          physical_suburb?: string | null;
          postal_address_name?: string | null;
          postal_city?: string | null;
          postal_country?: string | null;
          postal_postal_code?: string | null;
          postal_region?: string | null;
          postal_street_address?: string | null;
          postal_street_address_2?: string | null;
          postal_suburb?: string | null;
          reminder?: string | null;
          so_suffix?: string | null;
          supplier_code: string;
          tax_code?: string | null;
          taxable?: boolean | null;
          unleashed_product_code?: string | null;
          updated_at?: string;
          website?: string | null;
          zephyr_account_number?: string | null;
        };
        Update: {
          abn?: string | null;
          account_terms?: string | null;
          action_sheet_override?: string | null;
          created_at?: string;
          deactivated?: boolean;
          default_warehouse_code?: string | null;
          doc_name?: string | null;
          doc_template?: string | null;
          document_type?: string;
          dropdown_label?: string | null;
          email?: string | null;
          first_name?: string | null;
          footer_message?: string | null;
          has_authority_doc?: boolean;
          id?: never;
          is_obsoleted?: boolean;
          is_uniform_supplier?: boolean;
          kind?: string;
          last_name?: string | null;
          logo_path?: string | null;
          minimum_order_value?: number | null;
          mobile_number?: string | null;
          name?: string;
          notes?: string | null;
          office_phone?: string | null;
          payment_method?: string;
          phone_number?: string | null;
          physical_address_name?: string | null;
          physical_city?: string | null;
          physical_country?: string | null;
          physical_postal_code?: string | null;
          physical_region?: string | null;
          physical_street_address?: string | null;
          physical_street_address_2?: string | null;
          physical_suburb?: string | null;
          postal_address_name?: string | null;
          postal_city?: string | null;
          postal_country?: string | null;
          postal_postal_code?: string | null;
          postal_region?: string | null;
          postal_street_address?: string | null;
          postal_street_address_2?: string | null;
          postal_suburb?: string | null;
          reminder?: string | null;
          so_suffix?: string | null;
          supplier_code?: string;
          tax_code?: string | null;
          taxable?: boolean | null;
          unleashed_product_code?: string | null;
          updated_at?: string;
          website?: string | null;
          zephyr_account_number?: string | null;
        };
        Relationships: [];
      };
      telemarketing_calls: {
        Row: {
          called_at: string;
          called_by: string | null;
          id: string;
          new_status: string;
          note: string | null;
          shelter_id: string;
          task_id: string;
        };
        Insert: {
          called_at?: string;
          called_by?: string | null;
          id?: string;
          new_status: string;
          note?: string | null;
          shelter_id: string;
          task_id: string;
        };
        Update: {
          called_at?: string;
          called_by?: string | null;
          id?: string;
          new_status?: string;
          note?: string | null;
          shelter_id?: string;
          task_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "telemarketing_calls_called_by_fkey";
            columns: ["called_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "telemarketing_calls_shelter_id_fkey";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "telemarketing_calls_task_id_fkey";
            columns: ["task_id"];
            isOneToOne: false;
            referencedRelation: "telemarketing_tasks";
            referencedColumns: ["id"];
          },
        ];
      };
      telemarketing_campaigns: {
        Row: {
          created_at: string;
          created_by: string | null;
          deactivated: boolean;
          filters: Json | null;
          id: string;
          name: string;
        };
        Insert: {
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          filters?: Json | null;
          id?: string;
          name: string;
        };
        Update: {
          created_at?: string;
          created_by?: string | null;
          deactivated?: boolean;
          filters?: Json | null;
          id?: string;
          name?: string;
        };
        Relationships: [
          {
            foreignKeyName: "telemarketing_campaigns_created_by_fkey";
            columns: ["created_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      telemarketing_tasks: {
        Row: {
          assigned_at: string | null;
          assigned_to: string | null;
          campaign_id: string;
          created_at: string;
          id: string;
          last_updated_by: string | null;
          shelter_id: string;
          status: string;
          updated_at: string | null;
        };
        Insert: {
          assigned_at?: string | null;
          assigned_to?: string | null;
          campaign_id: string;
          created_at?: string;
          id?: string;
          last_updated_by?: string | null;
          shelter_id: string;
          status?: string;
          updated_at?: string | null;
        };
        Update: {
          assigned_at?: string | null;
          assigned_to?: string | null;
          campaign_id?: string;
          created_at?: string;
          id?: string;
          last_updated_by?: string | null;
          shelter_id?: string;
          status?: string;
          updated_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "telemarketing_tasks_assigned_to_fkey";
            columns: ["assigned_to"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "telemarketing_tasks_campaign_id_fkey";
            columns: ["campaign_id"];
            isOneToOne: false;
            referencedRelation: "telemarketing_campaigns";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "telemarketing_tasks_last_updated_by_fkey";
            columns: ["last_updated_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "telemarketing_tasks_shelter_id_fkey";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
      top_up_product_map: {
        Row: {
          created_at: string;
          deactivated: boolean;
          id: string;
          item_key: string;
          product_code: string;
          section: string;
          sort_order: number;
        };
        Insert: {
          created_at?: string;
          deactivated?: boolean;
          id?: string;
          item_key?: string;
          product_code: string;
          section: string;
          sort_order?: number;
        };
        Update: {
          created_at?: string;
          deactivated?: boolean;
          id?: string;
          item_key?: string;
          product_code?: string;
          section?: string;
          sort_order?: number;
        };
        Relationships: [];
      };
      top_up_so_rules: {
        Row: {
          created_at: string;
          deactivated: boolean;
          id: string;
          include_discount: boolean;
          line_mode: string;
          section: string;
          sort_order: number;
          suffix: string;
          variant: string;
        };
        Insert: {
          created_at?: string;
          deactivated?: boolean;
          id?: string;
          include_discount?: boolean;
          line_mode: string;
          section: string;
          sort_order?: number;
          suffix?: string;
          variant?: string;
        };
        Update: {
          created_at?: string;
          deactivated?: boolean;
          id?: string;
          include_discount?: boolean;
          line_mode?: string;
          section?: string;
          sort_order?: number;
          suffix?: string;
          variant?: string;
        };
        Relationships: [];
      };
      unleashed_product_map: {
        Row: {
          created_at: string;
          id: number;
          item_key: string;
          item_kind: string;
          notes: string | null;
          product_code: string;
          tax_code: string | null;
          updated_at: string;
          year_bucket: string;
        };
        Insert: {
          created_at?: string;
          id?: never;
          item_key: string;
          item_kind: string;
          notes?: string | null;
          product_code: string;
          tax_code?: string | null;
          updated_at?: string;
          year_bucket?: string;
        };
        Update: {
          created_at?: string;
          id?: never;
          item_key?: string;
          item_kind?: string;
          notes?: string | null;
          product_code?: string;
          tax_code?: string | null;
          updated_at?: string;
          year_bucket?: string;
        };
        Relationships: [
          {
            foreignKeyName: "unleashed_product_map_product_code_fkey";
            columns: ["product_code"];
            isOneToOne: false;
            referencedRelation: "unleashed_products";
            referencedColumns: ["product_code"];
          },
        ];
      };
      unleashed_products: {
        Row: {
          base_pack: string | null;
          base_unit: string | null;
          created_at: string;
          deactivated: boolean;
          id: number;
          product_code: string;
          product_description: string | null;
          product_group: string | null;
          updated_at: string;
        };
        Insert: {
          base_pack?: string | null;
          base_unit?: string | null;
          created_at?: string;
          deactivated?: boolean;
          id?: never;
          product_code: string;
          product_description?: string | null;
          product_group?: string | null;
          updated_at?: string;
        };
        Update: {
          base_pack?: string | null;
          base_unit?: string | null;
          created_at?: string;
          deactivated?: boolean;
          id?: never;
          product_code?: string;
          product_description?: string | null;
          product_group?: string | null;
          updated_at?: string;
        };
        Relationships: [];
      };
      unleashed_push_queue: {
        Row: {
          attempts: number;
          created_at: string;
          entity_id: string;
          entity_type: string;
          id: number;
          last_error: string | null;
          op: string;
          payload: Json | null;
          status: string;
          updated_at: string;
        };
        Insert: {
          attempts?: number;
          created_at?: string;
          entity_id: string;
          entity_type: string;
          id?: never;
          last_error?: string | null;
          op?: string;
          payload?: Json | null;
          status?: string;
          updated_at?: string;
        };
        Update: {
          attempts?: number;
          created_at?: string;
          entity_id?: string;
          entity_type?: string;
          id?: never;
          last_error?: string | null;
          op?: string;
          payload?: Json | null;
          status?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      unleashed_sync_log: {
        Row: {
          duration_ms: number | null;
          failed: number | null;
          id: number;
          message: string | null;
          processed: number | null;
          ran_at: string;
          retried: number | null;
          status: string;
        };
        Insert: {
          duration_ms?: number | null;
          failed?: number | null;
          id?: number;
          message?: string | null;
          processed?: number | null;
          ran_at?: string;
          retried?: number | null;
          status: string;
        };
        Update: {
          duration_ms?: number | null;
          failed?: number | null;
          id?: number;
          message?: string | null;
          processed?: number | null;
          ran_at?: string;
          retried?: number | null;
          status?: string;
        };
        Relationships: [];
      };
      user_shelters: {
        Row: {
          contact_label: string | null;
          created_at: string | null;
          deactivated: boolean;
          deactivated_at: string | null;
          email: string | null;
          first_name: string | null;
          id: string;
          is_primary: boolean | null;
          last_name: string | null;
          mobile: string | null;
          phone: string | null;
          shelter_code: string;
          shelter_id: string;
          unleashed_contact_guid: string | null;
          user_id: string | null;
        };
        Insert: {
          contact_label?: string | null;
          created_at?: string | null;
          deactivated?: boolean;
          deactivated_at?: string | null;
          email?: string | null;
          first_name?: string | null;
          id?: string;
          is_primary?: boolean | null;
          last_name?: string | null;
          mobile?: string | null;
          phone?: string | null;
          shelter_code: string;
          shelter_id: string;
          unleashed_contact_guid?: string | null;
          user_id?: string | null;
        };
        Update: {
          contact_label?: string | null;
          created_at?: string | null;
          deactivated?: boolean;
          deactivated_at?: string | null;
          email?: string | null;
          first_name?: string | null;
          id?: string;
          is_primary?: boolean | null;
          last_name?: string | null;
          mobile?: string | null;
          phone?: string | null;
          shelter_code?: string;
          shelter_id?: string;
          unleashed_contact_guid?: string | null;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "fk_user_shelters_user_id";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "user_shelters_shelter_id_fkey";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
    };
    Views: {
      requests_view: {
        Row: {
          approvals_confirmed_at: string | null;
          caseworker_note: string | null;
          cc_emails: Json | null;
          children: Json | null;
          contact_name: string | null;
          created_at: string | null;
          date: string | null;
          delivery_address_banned: boolean | null;
          delivery_address_id: string | null;
          delivery_address_override: string | null;
          delivery_address_resolved: string | null;
          delivery_address_verified: boolean | null;
          docs: Json | null;
          id: number | null;
          invoices: Json | null;
          mobile: string | null;
          non_registered_address: boolean | null;
          notes: Json | null;
          phone: string | null;
          ref_no: string | null;
          request_type: string | null;
          shelter_address: string | null;
          shelter_code: string | null;
          shelter_email: string | null;
          shelter_has_unread_comment: boolean | null;
          shelter_id: string | null;
          shelter_name: string | null;
          shelter_ref: string | null;
          state: string | null;
          status: Database["public"]["Enums"]["request_status_enum"] | null;
          updated_at: string | null;
          updated_by: string | null;
          updated_by_role: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "fk_requests_delivery_address_id";
            columns: ["delivery_address_id"];
            isOneToOne: false;
            referencedRelation: "delivery_addresses";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "fk_requests_shelter_id";
            columns: ["shelter_id"];
            isOneToOne: false;
            referencedRelation: "shelters";
            referencedColumns: ["id"];
          },
        ];
      };
    };
    Functions: {
      append_request_notes: {
        Args: {
          p_mark_unread?: boolean;
          p_notes: Json;
          p_request_id: number;
          p_updated_by: string;
        };
        Returns: Json;
      };
      append_to_docs: {
        Args: { new_doc: Json; request_id: string };
        Returns: Json;
      };
      claim_telemarketing_task: {
        Args: { p_task_id: string };
        Returns: undefined;
      };
      confirm_stock_top_up_atomic: {
        Args: { p_lines: Json; p_request_id: number; p_status: string };
        Returns: Json;
      };
      crm_dispatch_due: { Args: never; Returns: undefined };
      custom_access_token_hook: { Args: { event: Json }; Returns: Json };
      data_cleanup_execute: { Args: { p_criterion: string }; Returns: Json };
      data_cleanup_preview: { Args: { p_criterion: string }; Returns: Json };
      get_my_role: { Args: never; Returns: string };
      get_next_ref_no_preview: { Args: never; Returns: string };
      get_request_children: { Args: { p_request_id: number }; Returns: Json };
      get_request_participants: {
        Args: { p_request_id: number };
        Returns: {
          action_count: number;
          actions: string[];
          display_name: string;
          first_touch: string;
          last_touch: string;
          role: string;
          user_id: string;
        }[];
      };
      get_shelter_colleagues: {
        Args: { p_shelter_id: string };
        Returns: {
          display_name: string;
          user_email: string;
        }[];
      };
      get_user_role: { Args: { uid: string }; Returns: string };
      is_active_superuser: { Args: never; Returns: boolean };
      log_auth_failure: {
        Args: { p_action: string; p_details: string };
        Returns: undefined;
      };
      log_telemarketing_call: {
        Args: { p_new_status: string; p_task_id: string };
        Returns: undefined;
      };
      next_ref_no: { Args: never; Returns: string };
      profile_privileged_cols_unchanged: {
        Args: {
          p_admin_permissions: Json;
          p_deactivated: boolean;
          p_id: string;
          p_is_consultant: boolean;
          p_role: string;
        };
        Returns: boolean;
      };
      purge_shelters: { Args: { p_shelter_ids: string[] }; Returns: Json };
      record_request_decisions: {
        Args: { p_confirmed_at: string; p_request_id: number; p_rows: Json };
        Returns: number;
      };
      release_telemarketing_task: {
        Args: { p_task_id: string };
        Returns: undefined;
      };
      request_repeat_flags: {
        Args: { p_months?: number; p_request_ids: number[]; p_types?: string[] };
        Returns: {
          flagged_items: number;
          has_earlier: boolean;
          request_id: number;
          reviewed: boolean;
          strong: boolean;
        }[];
      };
      request_repeat_matches: {
        Args: { p_months?: number; p_request_id: number; p_types?: string[] };
        Returns: {
          child_index: number;
          item_id: string;
          item_kind: string;
          item_type: string;
          match_amount: number;
          match_child_index: number;
          match_created_at: string;
          match_decision: string;
          match_desc: string;
          match_item_id: string;
          match_ref_no: string;
          match_request_id: number;
          strong: boolean;
        }[];
      };
      request_shelter_update_ok: {
        Args: { p_confirmed_at: string; p_id: number; p_status: string };
        Returns: boolean;
      };
      reset_ref_no_seq_yearly: { Args: never; Returns: undefined };
      save_request_children: {
        Args: { p_children: Json; p_request_id: number };
        Returns: undefined;
      };
      set_line_picker_fields: {
        Args: { p_devices: Json; p_fees: Json };
        Returns: undefined;
      };
      set_line_so_numbers: {
        Args: { p_devices: Json; p_fees: Json };
        Returns: undefined;
      };
      set_next_ref_no: { Args: { p_value: number }; Returns: string };
      submit_stock_top_up_atomic: {
        Args: { p_lines: Json; p_request: Json };
        Returns: Json;
      };
      trigger_poll_school_locker: { Args: never; Returns: undefined };
      trigger_push_sales_orders: { Args: never; Returns: undefined };
      trigger_unleashed_sync_worker: { Args: never; Returns: undefined };
      uniform_line_total: {
        Args: { p_custom: Json; p_items: Json };
        Returns: number;
      };
      upsert_request_atomic: {
        Args: { p_children: Json; p_id: number; p_request: Json };
        Returns: Json;
      };
      user_can_access_child: {
        Args: { p_children_id: string };
        Returns: boolean;
      };
      user_can_access_request: {
        Args: { p_request_id: number };
        Returns: boolean;
      };
      user_can_read_child: { Args: { p_children_id: string }; Returns: boolean };
      user_can_read_request: {
        Args: { p_request_id: number };
        Returns: boolean;
      };
      user_can_submit_for_shelter_id: {
        Args: { p_shelter_id: string };
        Returns: boolean;
      };
      user_can_use_shelter_id: {
        Args: { p_shelter_id: string };
        Returns: boolean;
      };
      user_can_write_request: {
        Args: { p_request_id: number };
        Returns: boolean;
      };
      user_shares_shelter_with: {
        Args: { p_target_id: string };
        Returns: boolean;
      };
    };
    Enums: {
      approval_status_enum: "Pending" | "Approved" | "Declined" | "Needs Info";
      device_type_enum: "Laptop" | "iPad";
      fee_type_enum:
        | "Stationery & Textbooks"
        | "Stationery"
        | "Textbooks"
        | "Camp Fees"
        | "Resource Fees"
        | "Device Hire"
        | "School Activities"
        | "Non-school Activities"
        | "Swim Gear"
        | "Other (specify)";
      request_status_enum: "Draft" | "Submitted" | "Part Processed" | "Fully Processed";
    };
    CompositeTypes: {
      [_ in never]: never;
    };
  };
};

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">;

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">];

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R;
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] & DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R;
      }
      ? R
      : never
    : never;

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I;
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I;
      }
      ? I
      : never
    : never;

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U;
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U;
      }
      ? U
      : never
    : never;

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema["Enums"]
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never;

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never;

export const Constants = {
  graphql_public: {
    Enums: {},
  },
  public: {
    Enums: {
      approval_status_enum: ["Pending", "Approved", "Declined", "Needs Info"],
      device_type_enum: ["Laptop", "iPad"],
      fee_type_enum: [
        "Stationery & Textbooks",
        "Stationery",
        "Textbooks",
        "Camp Fees",
        "Resource Fees",
        "Device Hire",
        "School Activities",
        "Non-school Activities",
        "Swim Gear",
        "Other (specify)",
      ],
      request_status_enum: ["Draft", "Submitted", "Part Processed", "Fully Processed"],
    },
  },
} as const;
